import { Button } from '@app/components/various/Button';
import { Option } from '@app/components/various/Fields/Select';
import { AutocompleteField, Form, ToggleField } from '@app/components/various/Form';
import { Modal, StickyModalFooter } from '@app/components/various/Modal';
import { addToast } from '@app/components/various/Toasts';
import { getOrderId, getOrderNumber } from '@app/ducks';
import { useDownloadProductInformation } from '@app/services/hooks/documents/useDownloadProductInformation';
import { useSizeMappings } from '@app/services/hooks/units/useSizeMappings';
import { isEmpty } from '@app/utils/isEmpty';
import React from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { DownloadProductInformationFields } from './DownloadProductInformationFields';

const PRODUCT_SIZE_FIELD = 'bi_prod_size';

export interface DownloadProductInformationFormValues {
  fields: string[];
  includeProductsWithoutQuantity: boolean;
  sizeLocalizationIds: number[];
}

interface Props {
  isOpen: boolean;
  onSuccess?: VoidFunction;
  onClose: VoidFunction;
}

export const DownloadProductInformationModal = ({ isOpen, onSuccess, onClose }: Props) => {
  const { t } = useTranslation(['common', 'orders', 'linesheets']);
  const orderId = useSelector(getOrderId);
  const orderNumber = useSelector(getOrderNumber);
  const { mutate, isPending } = useDownloadProductInformation();
  const { data: sizeMappingsData, isLoading: isLoadingSizeMappings } = useSizeMappings();
  const sizeMappings = React.useMemo(() => sizeMappingsData?.data ?? [], [sizeMappingsData]);
  const defaultValues: DownloadProductInformationFormValues = {
    fields: [],
    includeProductsWithoutQuantity: false,
    sizeLocalizationIds: [],
  };

  const formMethods = useForm({ defaultValues });
  const { watch, setValue } = formMethods;
  const fields = watch('fields');

  const handleSubmit = (data: DownloadProductInformationFormValues) => {
    mutate(
      {
        data,
        orderId,
        orderNumber,
      },
      {
        onError: () => {
          addToast(t('orders:product_info_download_failure'), { type: 'error' });
        },
        onSuccess: () => {
          onSuccess?.();
          onClose();
        },
      },
    );
  };
  const isSizeFieldSelected = fields.includes(PRODUCT_SIZE_FIELD);

  React.useEffect(() => {
    if (isSizeFieldSelected) {
      return;
    }

    setValue('sizeLocalizationIds', []);
  }, [isSizeFieldSelected, setValue]);

  return (
    <Modal
      title={t('orders:product_info_download')}
      description={t('orders:product_info_description')}
      size="large"
      isOpen={isOpen}
      onClose={onClose}
      modalClosedCallback={() => formMethods.reset(defaultValues)}
    >
      <Form formMethods={formMethods} className="flex flex-col gap-6" onSubmit={handleSubmit}>
        <DownloadProductInformationFields isDisabled={isPending} />
        <AutocompleteField
          isLoadingOptions={isLoadingSizeMappings}
          label={t('linesheets:choose_units.label')}
          name="sizeLocalizationIds"
          isDisabled={!isSizeFieldSelected}
          helpMessage={t('orders:product_info_units_hint')}
          placeholder={t('linesheets:choose_units.placeholder')}
        >
          {sizeMappings.map(unit => (
            <Option key={unit.id} value={unit.id}>
              {unit.displayName}
            </Option>
          ))}
        </AutocompleteField>
        <ToggleField
          label={t('orders:include_products_without_quantity')}
          disabled={isPending}
          {...formMethods.register('includeProductsWithoutQuantity')}
          orientation="horizontal"
        />
        <StickyModalFooter>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="ghost" color="secondary" disabled={isPending} onClick={onClose}>
              {t('common:cancel')}
            </Button>
            <Button type="submit" isLoading={isPending} disabled={isEmpty(fields)}>
              {t('common:download')}
            </Button>
          </div>
        </StickyModalFooter>
      </Form>
    </Modal>
  );
};
